import { getAuth } from "firebase/auth";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  FormattedBuildingData,
  FormattedServiceRequestData,
  FilesReturnType,
  PrecomputedAmenityData,
  _PropertyPageTabType,
} from "~/types";
import {
  ENVS,
  INTENTS,
  getAuthWrapper,
  loadCookiesData,
  mergeAvailability,
  resolveActorUserName,
  getReadyAuthUser,
} from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";
import { apiClient, PropertyDetailsLoaderResult } from "~/services/apiClient";

export async function PropertyDetailsPageLoader({
  request,
  params,
}: LoaderFunctionArgs): Promise<PropertyDetailsPageLoaderType | Response> {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const { searchParams } = new URL(request.url);
  const tab = (searchParams.get("tab") ?? "details") as _PropertyPageTabType;
  const propertyID = params.propertyID || "";

  const result = await apiClient.loader<PropertyDetailsLoaderResult>(
    "property-details",
    {
      id: propertyID,
      ownerID,
      ownerType,
    },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load property details:", result.error);
    throw new Error(result.error ?? "Failed to load property details");
  }

  const {
    propertyDetails,
    buildingList,
    parcelList,
    amenityList,
    unitPlanList,
    serviceRequestList,
    tenantList,
    files: lambdaFiles,
    permissions,
    subscriptionPlan,
    hasServiceRequestAccess,
  } = result.data;

  const files: FilesReturnType = {
    all: [...lambdaFiles.files, ...lambdaFiles.images],
    files: lambdaFiles.files,
    images: lambdaFiles.images,
  } as FilesReturnType;

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const formattedBuildingList: FormattedBuildingData[] = buildingList.map(
    (building) => ({
      buildingDetails: building,
      propertyDetails: propertyDetails,
      unitList: [],
    }),
  );

  const formattedAmenityList: PrecomputedAmenityData[] = (amenityList ?? []).map(
    (amenity) => {
      const formattedAmenityDetails = {
        amenityDetails: amenity,
        propertyDetails: propertyDetails,
        reservations: [],
      };
      return {
        formattedAmenityDetails,
        availability: mergeAvailability(formattedAmenityDetails),
      };
    },
  );

  const formattedServiceRequestList: FormattedServiceRequestData[] =
    serviceRequestList.map((sr) => ({
      ...sr,
      entityDetails: {
        property: { success: true, data: propertyDetails },
      },
    }));

  const userName = await resolveActorUserName(user, authAccessInfo);

  return {
    propertyDetails,
    tab,
    buildingList: formattedBuildingList,
    parcelList: parcelList ?? [],
    amenityList: formattedAmenityList,
    unitPlanList,
    files,
    formattedServiceRequestList,
    tenantList,
    ownerID,
    ownerType,
    userName,
    canCreateRequest: permissions.canCreateServiceRequest,
    canDeleteProperty: permissions.canDeleteProperty,
    canEditProperty: permissions.canEditProperty,
    canReadProperty: permissions.canReadProperty,
    isAdminOrSuperAdmin,
    isAuthenticated: true,
    subscriptionPlan,
    hasServiceRequestAccess,
  };
}

export type PropertyDetailsPageLoaderType = {
  propertyDetails: APITypes.Property;
  tab: _PropertyPageTabType;
  buildingList: FormattedBuildingData[];
  parcelList: APITypes.Parcel[];
  amenityList: PrecomputedAmenityData[];
  unitPlanList: PropertyDetailsLoaderResult["unitPlanList"];
  files: FilesReturnType;
  formattedServiceRequestList: FormattedServiceRequestData[];
  tenantList: APITypes.Tenant[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  canCreateRequest: boolean;
  canDeleteProperty: boolean;
  canEditProperty: boolean;
  canReadProperty: boolean;
  isAdminOrSuperAdmin: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
};

function parseBody(formData: FormData): Record<string, unknown> {
  const body = formData.get("body");
  if (typeof body !== "string") return {};
  try {
    return JSON.parse(body) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export const PropertyDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const propertyID = params.propertyID || "";
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType } = authAccessInfo.data;
  const userName = await resolveActorUserName(user, authAccessInfo);

  switch (intent) {
    case INTENTS._edit_property: {
      return withEntityCacheInvalidation(
        [{ entity: "property", entityID: propertyID }],
        async () => {
          const payload = parseBody(formData);
          const result = await apiClient.sendIntent<APITypes.Property>(
            INTENTS._edit_property,
            { propertyID, id: propertyID, ...payload },
          );
          if (!result.success) {
            return { success: false, error: result.error };
          }
          return result;
        },
      );
    }
    case INTENTS._add_service_request: {
      const payload = parseBody(formData) as APITypes.CreateServiceRequestInput;
      const result = await apiClient.sendIntent<APITypes.ServiceRequest>(
        INTENTS._add_service_request,
        {
          entityID: propertyID,
          entityType: APITypes.EntityType.Property,
          creatorID: user.uid,
          creatorName: userName,
          ownerID,
          ownerType,
          title: payload.title,
          description: payload.description,
          type: payload.type,
          status: payload.status,
          emergency: payload.emergency,
          isUpgrade: payload.isUpgrade,
        },
      );
      if (!result.success) {
        return { success: false, error: result.error };
      }
      return result;
    }
    case INTENTS._remove_property: {
      return withEntityCacheInvalidation(
        [{ entity: "property", entityID: propertyID }],
        async () => {
          const payload = parseBody(formData);
          const result = await apiClient.sendIntent(INTENTS._remove_property, {
            propertyID,
            _version: payload._version,
          });
          if (!result.success) {
            return { success: false, error: result.error };
          }
          return redirect("/property");
        },
      );
    }
    case INTENTS._delete_file: {
      const key = formData.get("body") as string;
      const result = await S3ServiceLambda.remove({ key });
      return result;
    }
  }
  return null;
};
